import * as THREE from 'three';
import { OrbManager } from '../../orbs/OrbManager';
import { global } from '../../core/GlobalState';
const queue = [];
let rafHandle = null;
const MAX_PER_FRAME = 10;
const MIN_PER_FRAME = 2;
const FRAME_BUDGET_MS = 3.5;
const MIN_FRAME_BUDGET_MS = 1.75;
const MAX_FRAME_BUDGET_MS = 4.5;
const COST_SMOOTHING = 0.25;
const COST_TARGET_MS = 0.8;
let adaptivePerFrame = MAX_PER_FRAME;
let adaptiveFrameBudget = FRAME_BUDGET_MS;
let smoothedSpawnCost = 0;
const jitter = new THREE.Vector3();
const workingDirection = new THREE.Vector3();
function requestFrame(cb) {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        return window.requestAnimationFrame(cb);
    }
    return setTimeout(() => cb(now()), 16);
}
function cancelFrame(handle) {
    if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(handle);
    }
    else {
        clearTimeout(handle);
    }
}
function now() {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        return performance.now();
    }
    return Date.now();
}
function insertTask(task) {
    let low = 0;
    let high = queue.length;
    while (low < high) {
        const mid = (low + high) >> 1;
        if (queue[mid].availableAt <= task.availableAt) {
            low = mid + 1;
        }
        else {
            high = mid;
        }
    }
    queue.splice(low, 0, task);
}
function schedulePump() {
    if (rafHandle !== null)
        return;
    rafHandle = requestFrame(processQueue);
}
function processQueue(time) {
    rafHandle = null;
    const manager = OrbManager.getInstance();
    const frameStart = now();
    let processed = 0;
    while (queue.length > 0) {
        const task = queue[0];
        if (task.availableAt > time)
            break;
        queue.shift();
        try {
            const spawnStart = now();
            spawnOrb(manager, task);
            recordSpawnCost(now() - spawnStart);
            task.resolve();
        }
        catch (error) {
            task.reject(error);
        }
        processed += 1;
        if (processed >= adaptivePerFrame)
            break;
        if (now() - frameStart >= adaptiveFrameBudget)
            break;
    }
    if (queue.length > 0) {
        if (queue.length > 180) {
            if (adaptivePerFrame > MIN_PER_FRAME)
                adaptivePerFrame = Math.max(MIN_PER_FRAME, adaptivePerFrame - 1);
            adaptiveFrameBudget = Math.max(MIN_FRAME_BUDGET_MS, adaptiveFrameBudget - 0.25);
        }
        else if (queue.length < 40 && adaptivePerFrame < MAX_PER_FRAME) {
            adaptivePerFrame = Math.min(MAX_PER_FRAME, adaptivePerFrame + 1);
            adaptiveFrameBudget = Math.min(MAX_FRAME_BUDGET_MS, adaptiveFrameBudget + 0.1);
        }
        schedulePump();
    }
}
function recordSpawnCost(costMs) {
    if (!Number.isFinite(costMs))
        return;
    if (smoothedSpawnCost === 0)
        smoothedSpawnCost = costMs;
    else
        smoothedSpawnCost = smoothedSpawnCost * (1 - COST_SMOOTHING) + costMs * COST_SMOOTHING;
    if (smoothedSpawnCost > COST_TARGET_MS && adaptivePerFrame > MIN_PER_FRAME) {
        adaptivePerFrame = Math.max(MIN_PER_FRAME, adaptivePerFrame - 1);
        adaptiveFrameBudget = Math.max(MIN_FRAME_BUDGET_MS, adaptiveFrameBudget - 0.5);
    }
    else if (smoothedSpawnCost < COST_TARGET_MS * 0.6 && adaptivePerFrame < MAX_PER_FRAME) {
        adaptivePerFrame = Math.min(MAX_PER_FRAME, adaptivePerFrame + 1);
        adaptiveFrameBudget = Math.min(MAX_FRAME_BUDGET_MS, adaptiveFrameBudget + 0.25);
    }
}
function spawnOrb(manager, task) {
    var _a, _b, _c, _d;
    const { orb, origin, direction, sprayCoefficient, baseVelocity, sceneName, originAttachment } = task;
    const spawnOrigin = (_d = (_c = (_b = (_a = originAttachment === null || originAttachment === void 0 ? void 0 : originAttachment.group) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.clone) === null || _c === void 0 ? void 0 : _c.call(_b)) !== null && _d !== void 0 ? _d : origin.clone();
    orb.setPosition(spawnOrigin);
    jitter.set((Math.random() - 0.5) * sprayCoefficient, (Math.random() - 0.5) * sprayCoefficient, (Math.random() - 0.5) * sprayCoefficient);
    workingDirection.copy(direction).add(jitter).normalize();
    const speed = baseVelocity * (Math.random() * 2.6 + 0.5);
    orb.setVelocity(workingDirection.multiplyScalar(speed));
    const targetSceneName = sceneName !== null && sceneName !== void 0 ? sceneName : global.sceneManager.getCurrentSceneName();
    manager.addOrbToScene(orb, targetSceneName);
}
export function scheduleOrbSpawn(task) {
    return new Promise((resolve, reject) => {
        insertTask(Object.assign(Object.assign({}, task), { resolve, reject }));
        schedulePump();
    });
}
export function clearOrbSpawnQueue() {
    queue.length = 0;
    if (rafHandle !== null) {
        cancelFrame(rafHandle);
        rafHandle = null;
    }
    resetOrbSpawnThrottles();
}
export function resetOrbSpawnThrottles() {
    adaptivePerFrame = MAX_PER_FRAME;
    adaptiveFrameBudget = FRAME_BUDGET_MS;
    smoothedSpawnCost = 0;
}
